import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, Crown, ShieldCheck, Lock, ArrowRight } from 'lucide-react';
type PlanId = 'standard' | 'pro';
const PLANS = {
  standard: {
    id: 'standard' as PlanId,
    name: 'MEGAPACK BÁSQUET RESUELTO',
    short: 'Megapack Estándar',
    desc: '6 tomos + 6 bonos estratégicos',
    price: '$19.990',
    oldPrice: '$29.990',
    pages: '540 páginas',
    url: 'https://checkout.abastecedordigital.shop/cart/52085586035008:1'
  },
  pro: {
    id: 'pro' as PlanId,
    name: 'MEGAPACK BÁSQUET RESUELTO PRO',
    short: 'Megapack PRO',
    desc: '6 tomos + 6 bonos + 2 tomos PRO exclusivos',
    price: '$29.990',
    oldPrice: '$43.990',
    pages: '660 páginas',
    savings: 'Ahorrás $14.000',
    url: 'https://checkout.abastecedordigital.shop/cart/52085586166080:1'
  }
};
interface PricingSelectorProps {
  variant?: 'full' | 'compact';
}
export function PricingSelector({
  variant = 'full'
}: PricingSelectorProps) {
  const [selected, setSelected] = useState<PlanId>('pro');
  const plan = PLANS[selected];
  const handleCheckout = () => {
    window.location.href = plan.url;
  };
  return <div className="w-full max-w-2xl mx-auto" data-id="element-168">
      <div className="space-y-3" data-id="element-169">
        {/* PRO Option (always rendered first - preselected) */}
        <PlanCard plan={PLANS.pro} isSelected={selected === 'pro'} isPro onSelect={() => setSelected('pro')} variant={variant} data-id="element-170" />

        {/* Standard Option */}
        <PlanCard plan={PLANS.standard} isSelected={selected === 'standard'} onSelect={() => setSelected('standard')} variant={variant} data-id="element-171" />
      </div>

      {/* Shared CTA */}
      <motion.button onClick={handleCheckout} whileTap={{
      scale: 0.98
    }} whileHover={{
      scale: 1.01
    }} className={`w-full mt-5 md:mt-6 font-display text-xl md:text-2xl py-4 md:py-5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-[0_6px_0_rgba(0,0,0,0.2)] active:shadow-[0_0px_0_rgba(0,0,0,0.2)] active:translate-y-1.5
          ${selected === 'pro' ? 'bg-brand-gold hover:bg-yellow-500 text-brand-dark' : 'bg-brand-orange hover:bg-orange-600 text-white'}`} data-id="element-172">
        <span data-id="element-173">
          COMPRAR {plan.short.toUpperCase()} · {plan.price}
        </span>
        <ArrowRight size={22} className="shrink-0" data-id="element-174" />
      </motion.button>

      <div className="flex flex-wrap justify-center gap-3 md:gap-6 mt-4 md:mt-5 text-[11px] md:text-xs font-bold text-gray-500" data-id="element-175">
        <div className="flex items-center gap-1.5" data-id="element-176">
          <Lock size={13} data-id="element-177" /> Pago seguro
        </div>
        <div className="flex items-center gap-1.5" data-id="element-178">
          <Zap size={13} data-id="element-179" /> Acceso en 5 min
        </div>
        <div className="flex items-center gap-1.5" data-id="element-180">
          <ShieldCheck size={13} data-id="element-181" /> Garantía 7 días
        </div>
      </div>
    </div>;
}
interface PlanCardProps {
  plan: typeof PLANS.standard | typeof PLANS.pro;
  isSelected: boolean;
  isPro?: boolean;
  onSelect: () => void;
  variant: 'full' | 'compact';
}
function PlanCard({
  plan,
  isSelected,
  isPro,
  onSelect,
  variant
}: PlanCardProps) {
  return <motion.button type="button" onClick={onSelect} whileTap={{
    scale: 0.99
  }} animate={{
    scale: isSelected ? 1 : 0.98,
    opacity: isSelected ? 1 : 0.85
  }} transition={{
    duration: 0.2
  }} className={`relative w-full text-left rounded-2xl overflow-hidden transition-all
        ${isSelected ? isPro ? 'bg-brand-dark border-2 border-brand-gold shadow-[0_10px_30px_-10px_rgba(212,162,76,0.6)]' : 'bg-white border-2 border-brand-orange shadow-lg' : 'bg-white border-2 border-gray-200 hover:border-gray-300'}`} data-id="element-182">
      {/* "Más Elegido" Stripe on PRO */}
      {isPro && <div className={`absolute top-0 right-0 ${isSelected ? 'bg-brand-gold text-brand-dark' : 'bg-gray-300 text-gray-600'} text-[10px] md:text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider flex items-center gap-1 transition-colors`} data-id="element-183">
          <Crown size={11} data-id="element-184" /> Más Elegido
        </div>}

      <div className="flex items-center gap-3 md:gap-4 p-4 md:p-5" data-id="element-185">
        {/* Radio Indicator */}
        <div className={`shrink-0 w-6 h-6 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-all
          ${isSelected ? isPro ? 'border-brand-gold bg-brand-gold' : 'border-brand-orange bg-brand-orange' : 'border-gray-300 bg-white'}`} data-id="element-186">
          {isSelected && <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} data-id="element-187">
              <Check size={14} strokeWidth={3} className={isPro ? 'text-brand-dark' : 'text-white'} data-id="element-188" />
            </motion.div>}
        </div>

        {/* Plan Info */}
        <div className="flex-grow min-w-0" data-id="element-189">
          <div className="flex items-center gap-2 mb-0.5" data-id="element-190">
            <h3 className={`font-display text-sm md:text-lg leading-tight truncate ${isSelected && isPro ? 'text-white' : 'text-brand-dark'}`} data-id="element-191">
              {plan.short.toUpperCase()}
              {isPro && <span className={`ml-1 ${isSelected ? 'text-brand-gold' : 'text-gray-400'}`} data-id="element-192">
                  ★
                </span>}
            </h3>
          </div>
          <p className={`text-[11px] md:text-sm font-medium leading-snug ${isSelected && isPro ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-193">
            {plan.desc}
          </p>
          {variant === 'full' && <p className={`text-[10px] md:text-xs mt-1 font-bold uppercase tracking-wider ${isSelected && isPro ? 'text-brand-gold' : isSelected ? 'text-brand-orange' : 'text-gray-400'}`} data-id="element-194">
              {plan.pages}
              {isPro && plan.savings && <span className="ml-2 text-green-500" data-id="element-195">· {plan.savings}</span>}
            </p>}
        </div>

        {/* Price */}
        <div className="text-right shrink-0" data-id="element-196">
          <div className={`text-[10px] md:text-xs line-through font-medium ${isSelected && isPro ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-197">
            {plan.oldPrice}
          </div>
          <div className={`font-display text-2xl md:text-3xl leading-none ${isSelected && isPro ? 'text-brand-gold' : isSelected ? 'text-brand-dark' : 'text-gray-700'}`} data-id="element-198">
            {plan.price}
          </div>
        </div>
      </div>
    </motion.button>;
}